<template>
  <div v-if="lesson">
    <LessonHeader
      :lesson-title="lesson.lesson_title"
      :lesson-order="lesson.lesson_order"
      :cover-image="coverImage"
    />
    <LessonVideo
      :video-id="lesson.lesson_video_url"
      :title="lesson.lesson_title"
    />
    <LessonContent ref="lessonContentRef" :blocks="lesson.lesson_content" />
    <SubmitForm :lesson-token="lesson.lesson_token" :lesson="lesson" :lesson-content-ref="lessonContentRef" />
    <CtaBlock />

    <!-- Lesson navigation -->
    <nav class="bg-secondary lg:px-48 md:px-12 px-4 py-8 flex justify-between items-center font-montserrat border-t border-black/10">
      <NuxtLink
        v-if="prevLesson"
        :to="localePath(`/lesson/${prevLesson.lesson_order}`)"
        class="growing-underline"
      >
        ← {{ t('lesson.prevLesson') }}
      </NuxtLink>
      <span v-else />
      <NuxtLink
        :to="localePath('/') + '#lessons'"
        class="text-sm opacity-50 hover:opacity-100 transition-opacity"
      >
        ≡ {{ t('lessons.title') }}
      </NuxtLink>
      <NuxtLink
        v-if="nextLesson"
        :to="localePath(`/lesson/${nextLesson.lesson_order}`)"
        class="growing-underline"
      >
        {{ t('lesson.nextLesson') }} →
      </NuxtLink>
    </nav>
  </div>

  <div v-else-if="loading" class="min-h-screen bg-secondary flex justify-center items-center">
    <div class="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin" />
  </div>

  <div v-else class="min-h-screen bg-secondary flex justify-center items-center font-montserrat text-gray-400">
    {{ t('lesson.notFound') }}
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { ref } from 'vue'

const route = useRoute()
const { t } = useI18n()
const localePath = useLocalePath()
const { fetchLesson, fetchLessons, lesson, lessons, loading } = useLesson()
const lessonContentRef = ref<{ getResults: () => Array<Record<string, unknown>> } | null>(null)

const slug = route.params.slug as string

await Promise.all([fetchLesson(slug), fetchLessons()])

// Cover image — перший блок type "19" в lesson_content
const coverImage = computed(() =>
  (lesson.value?.lesson_content ?? []).find((b: any) => b.type === '19')?.src ?? null
)

const currentOrder = computed(() => lesson.value?.lesson_order ?? 0)
const prevLesson = computed(() =>
  lessons.value.find(l => l.lesson_order === currentOrder.value - 1)
)
const nextLesson = computed(() =>
  lessons.value.find(l => l.lesson_order === currentOrder.value + 1)
)

const lessonTitle = lesson.value
  ? `${t('lessons.lesson')} ${lesson.value.lesson_order}: ${lesson.value.lesson_title}`
  : t('meta.lessonTitle')
const lessonDesc = lesson.value?.lesson_summary ?? t('meta.homeDesc')
const lessonImage = coverImage.value ?? 'https://osnovy.bibleawr.com/og-image.jpg'
const lessonUrl = `https://osnovy.bibleawr.com/lesson/${slug}`

useHead({
  title: lessonTitle,
  meta: [
    { name: 'description', content: lessonDesc },
    { property: 'og:title', content: lessonTitle },
    { property: 'og:description', content: lessonDesc },
    { property: 'og:image', content: lessonImage },
    { property: 'og:url', content: lessonUrl },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:title', content: lessonTitle },
    { name: 'twitter:description', content: lessonDesc },
    { name: 'twitter:image', content: lessonImage },
  ],
})
</script>
